import { FC, useState } from 'react';

import { Link, router } from '@inertiajs/react';
import { Clock3 } from 'lucide-react';

import { OrderRevisionDiff } from './OrderRevisionDiff';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '../../../components/ui/Alert';
import { Button } from '../../../components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/Dialog';
import {
    profileOrderRevisionActionRoute,
    profileOrderRevisionRoute,
    Routes,
} from '../../../models/routes';
import { Order } from '../../../types';

interface Props {
    order: Order;
    role: 'seller' | 'buyer';
}

export const OrderRevisionPanel: FC<Props> = ({ order, role }) => {
    const revision = order.pending_revision;
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [deliveryProfileRequired, setDeliveryProfileRequired] =
        useState(false);

    if (!revision) return null;

    const submit = (action: 'accept' | 'reject') => {
        setError('');
        setDeliveryProfileRequired(false);
        setProcessing(true);
        router.post(
            profileOrderRevisionActionRoute(order.id, action),
            {},
            {
                preserveScroll: true,
                onError: (errors) => {
                    setError(
                        String(
                            errors.general ||
                                'Не вдалося обробити пропозицію змін.',
                        ),
                    );
                    setDeliveryProfileRequired(
                        Boolean(errors.delivery_profile_required),
                    );
                },
                onFinish: () => setProcessing(false),
                onSuccess: () => setConfirmationOpen(false),
            },
        );
    };

    const cancel = () => {
        setError('');
        setProcessing(true);
        router.delete(profileOrderRevisionRoute(order.id), {
            preserveScroll: true,
            onError: (errors) =>
                setError(
                    String(
                        errors.general ||
                            'Не вдалося скасувати пропозицію змін.',
                    ),
                ),
            onFinish: () => setProcessing(false),
        });
    };

    return (
        <>
            <Alert variant="warning" className="mt-4">
                <Clock3 className="h-4 w-4" />
                <AlertTitle>
                    {role === 'seller'
                        ? 'Очікується підтвердження змін покупцем'
                        : 'Продавець запропонував зміни до замовлення'}
                </AlertTitle>
                <AlertDescription className="space-y-4">
                    <p>
                        {role === 'seller'
                            ? 'Ви запропонували зміни. Передача або відправлення стане доступним після рішення покупця.'
                            : 'Перегляньте точний склад, доставку та суму перед підтвердженням.'}
                    </p>
                    <OrderRevisionDiff revision={revision} />
                    {error && (
                        <p className="font-medium text-destructive">{error}</p>
                    )}
                    {role === 'seller' ? (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={processing}
                            onClick={cancel}
                        >
                            Скасувати пропозицію змін
                        </Button>
                    ) : (
                        <div className="flex flex-col gap-2 sm:flex-row">
                            <Button
                                type="button"
                                size="sm"
                                disabled={processing}
                                onClick={() => setConfirmationOpen(true)}
                            >
                                Підтвердити зміни
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={processing}
                                onClick={() => submit('reject')}
                            >
                                Відхилити
                            </Button>
                        </div>
                    )}
                </AlertDescription>
            </Alert>

            <Dialog open={confirmationOpen} onOpenChange={setConfirmationOpen}>
                <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-left">
                            Підтвердити зміни до замовлення?
                        </DialogTitle>
                        <DialogDescription className="text-left">
                            Після підтвердження новий склад, сума та спосіб
                            доставки будуть застосовані до замовлення.
                        </DialogDescription>
                    </DialogHeader>
                    {error && (
                        <div className="space-y-3">
                            <p className="text-sm font-medium text-destructive">
                                {error}
                            </p>
                            {deliveryProfileRequired && (
                                <Button asChild size="sm" variant="outline">
                                    <Link
                                        href={`${Routes.ProfileEdit}?section=delivery#delivery-details`}
                                    >
                                        Заповнити дані доставки
                                    </Link>
                                </Button>
                            )}
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:space-x-0">
                        <Button
                            type="button"
                            variant="outline"
                            disabled={processing}
                            onClick={() => setConfirmationOpen(false)}
                        >
                            Скасувати
                        </Button>
                        <Button
                            type="button"
                            disabled={processing}
                            onClick={() => submit('accept')}
                        >
                            {processing
                                ? 'Підтвердження…'
                                : 'Підтвердити зміни'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
